// ST-1705 -Claude- Nearrme platform team management.
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { createUserProfile, listCompanies, listNearrmeTeam, listRoles, updateUserProfile, updateUserRole } from '../api';
import { ImageRequirementNote, imageSpecs } from '../imageSpecs';

export function AdminNearrmeTeamPage() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [roleEdits, setRoleEdits] = useState<Record<number, string>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: 'NearrmeUser',
    imageUrl: '',
    assignedCompanyCodes: [] as string[],
    isActive: true,
  });
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: 'NearrmeUser',
    imageUrl: '',
    assignedCompanyCodes: [] as string[],
  });
  const team = useQuery({ queryKey: ['admin', 'nearrme-team'], queryFn: listNearrmeTeam });
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: listRoles });
  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const roleMutation = useMutation({
    mutationFn: ({ userProfileId, role }: { userProfileId: number; role: string }) =>
      updateUserRole(userProfileId, role, '__all'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'nearrme-team'] }),
  });
  const platformRoles = (roles.data ?? []).filter((role) => role.adminOnly);
  const createMutation = useMutation({
    mutationFn: () => createUserProfile({
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phoneNumber: form.phoneNumber,
      companyCode: '__all',
      role: form.role,
      imageUrl: form.imageUrl,
      assignedCompanyCodes: form.assignedCompanyCodes.length ? form.assignedCompanyCodes : ['__all'],
    }),
    onSuccess: () => {
      setFormOpen(false);
      setForm({ firstName: '', lastName: '', email: '', phoneNumber: '', role: 'NearrmeUser', imageUrl: '', assignedCompanyCodes: [] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nearrme-team'] });
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ userProfileId }: { userProfileId: number }) => updateUserProfile(userProfileId, {
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      email: editForm.email,
      phoneNumber: editForm.phoneNumber,
      companyCode: '__all',
      role: editForm.role,
      imageUrl: editForm.imageUrl,
      assignedCompanyCodes: editForm.assignedCompanyCodes.length ? editForm.assignedCompanyCodes : ['__all'],
      isActive: editForm.isActive,
    }),
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'nearrme-team'] });
    },
  });

  const startEdit = (member: NonNullable<typeof team.data>[number]) => {
    setEditingId(member.userProfileId ?? null);
    setEditForm({
      firstName: member.firstName ?? '',
      lastName: member.lastName ?? '',
      email: member.email ?? '',
      phoneNumber: member.phoneNumber ?? '',
      role: member.role ?? 'NearrmeUser',
      imageUrl: member.imageUrl ?? '',
      assignedCompanyCodes: member.assignedCompanyCodes?.length ? [...member.assignedCompanyCodes] : [member.companyCode ?? '__all'],
      isActive: member.isActive ?? true,
    });
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Nearrme team</h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Platform staff who operate Nearrme across all companies, consumers, orders, support, and configuration.
          </p>
        </div>
        <Button variant="primary" onClick={() => setFormOpen((value) => !value)}>
          {formOpen ? 'Close' : 'Add user'}
        </Button>
      </div>

      {formOpen && (
        <Card padded className="mt-4">
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Add Nearrme user</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Field label="First name" value={form.firstName} onChange={(value) => setForm({ ...form, firstName: value })} />
            <Field label="Last name" value={form.lastName} onChange={(value) => setForm({ ...form, lastName: value })} />
            <Field label="Email" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} />
            <Field label="Phone" value={form.phoneNumber} onChange={(value) => setForm({ ...form, phoneNumber: value })} />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Role</span>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              >
                {platformRoles.map((role) => (
                  <option key={role.roleCode} value={role.roleCode}>{role.displayName}</option>
                ))}
              </select>
            </label>
            <div>
              <Field label="User image URL" value={form.imageUrl} onChange={(value) => setForm({ ...form, imageUrl: value })} />
              <ImageRequirementNote spec={imageSpecs.userProfile} />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Assigned companies</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm dark:border-slate-800">
                <input
                  type="checkbox"
                  checked={form.assignedCompanyCodes.includes('__all')}
                  onChange={(e) => setForm({ ...form, assignedCompanyCodes: e.target.checked ? ['__all'] : [] })}
                />
                All companies
              </label>
              {(companies.data ?? []).map((company) => (
                <label key={company.companyCode ?? ''} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm dark:border-slate-800">
                  <input
                    type="checkbox"
                    disabled={form.assignedCompanyCodes.includes('__all')}
                    checked={form.assignedCompanyCodes.includes(company.companyCode ?? '')}
                    onChange={(e) => {
                      const code = company.companyCode ?? '';
                      const next = new Set(form.assignedCompanyCodes);
                      if (e.target.checked) next.add(code);
                      else next.delete(code);
                      setForm({ ...form, assignedCompanyCodes: [...next] });
                    }}
                  />
                  {company.companyCode}
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button variant="primary" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
              Create user
            </Button>
            {createMutation.error && (
              <span className="text-sm text-red-600 dark:text-red-400">{(createMutation.error as Error).message}</span>
            )}
          </div>
        </Card>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card padded>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Platform roles</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {platformRoles.map((role) => (
              <div key={role.roleCode} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                <div className="font-semibold text-slate-900 dark:text-slate-100">{role.displayName}</div>
                <div className="mt-1 text-xs font-semibold text-brand-700 dark:text-brand-300">
                  {role.profileType} - {role.scopeLevel}
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-400">{role.profileSummary}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {role.permissions.slice(0, 4).map((permission) => (
                    <span key={permission} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card padded>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Scope</h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Nearrme team roles are only Nearrme Admin and Nearrme User. Company and store staff are managed under Company Team, and consumers stay under Consumers.
          </p>
        </Card>
      </div>

      {team.isLoading && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3].map((i) => <LoadingSkeleton key={i} className="h-12 w-full" />)}
        </div>
      )}

      {team.error && (
        <Card padded className="mt-6">
          <div className="text-sm text-red-600 dark:text-red-400">{(team.error as Error).message}</div>
        </Card>
      )}

      {team.data && (
        team.data.length === 0 ? (
          <EmptyState title="No Nearrme team members" />
        ) : (
          <Card padded={false} className="mt-6 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Profile</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Email</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Phone</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Companies</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Role</th>
                  <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {team.data.map((member) => {
                  const userProfileId = member.userProfileId ?? 0;
                  const selectedRole = roleEdits[userProfileId] ?? member.role ?? '';
                  return (
                  <Fragment key={member.userProfileId ?? member.email}>
                  <tr
                    key={member.userProfileId ?? member.email}
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest('button, select, input, a')) return;
                      startEdit(member);
                    }}
                    tabIndex={0}
                    className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {member.imageUrl ? (
                          <img src={member.imageUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
                        ) : (
                          <div className="grid h-10 w-10 rounded-full bg-slate-200 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300 place-items-center">
                            {[member.firstName?.[0], member.lastName?.[0]].filter(Boolean).join('') || 'U'}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">{[member.firstName, member.lastName].filter(Boolean).join(' ') || '-'}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">User profile #{member.userProfileId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{member.email}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{member.phoneNumber}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                      {(member.assignedCompanyCodes?.length ? member.assignedCompanyCodes : [member.companyCode]).join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={selectedRole}
                        onChange={(e) => setRoleEdits((current) => ({ ...current, [userProfileId]: e.target.value }))}
                        className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      >
                        {platformRoles.map((role) => (
                          <option key={role.roleCode} value={role.roleCode}>{role.displayName}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!member.userProfileId || selectedRole === member.role || roleMutation.isPending}
                          onClick={() => roleMutation.mutate({ userProfileId, role: selectedRole })}
                        >
                          Save role
                        </Button>
                        <Button size="sm" variant="secondary" disabled={!member.userProfileId} onClick={() => startEdit(member)}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {editingId === userProfileId && (
                    <tr>
                      <td colSpan={6} className="bg-slate-50 px-4 py-4 dark:bg-slate-900">
                        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                          <div className="grid gap-3 md:grid-cols-2">
                            <Field label="First name" value={editForm.firstName} onChange={(value) => setEditForm({ ...editForm, firstName: value })} />
                            <Field label="Last name" value={editForm.lastName} onChange={(value) => setEditForm({ ...editForm, lastName: value })} />
                            <Field label="Email" type="email" value={editForm.email} onChange={(value) => setEditForm({ ...editForm, email: value })} />
                            <Field label="Phone" value={editForm.phoneNumber} onChange={(value) => setEditForm({ ...editForm, phoneNumber: value })} />
                            <label className="block">
                              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Role</span>
                              <select
                                value={editForm.role}
                                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                              >
                                {platformRoles.map((role) => (
                                  <option key={role.roleCode} value={role.roleCode}>{role.displayName}</option>
                                ))}
                              </select>
                            </label>
                            <div>
                              <Field label="User image URL" value={editForm.imageUrl} onChange={(value) => setEditForm({ ...editForm, imageUrl: value })} />
                              <ImageRequirementNote spec={imageSpecs.userProfile} />
                            </div>
                          </div>
                          <div className="mt-4">
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">Assigned companies</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm dark:border-slate-800">
                                <input
                                  type="checkbox"
                                  checked={editForm.assignedCompanyCodes.includes('__all')}
                                  onChange={(e) => setEditForm({ ...editForm, assignedCompanyCodes: e.target.checked ? ['__all'] : [] })}
                                />
                                All companies
                              </label>
                              {(companies.data ?? []).map((company) => (
                                <label key={company.companyCode ?? ''} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm dark:border-slate-800">
                                  <input
                                    type="checkbox"
                                    disabled={editForm.assignedCompanyCodes.includes('__all')}
                                    checked={editForm.assignedCompanyCodes.includes(company.companyCode ?? '')}
                                    onChange={(e) => {
                                      const code = company.companyCode ?? '';
                                      const next = new Set(editForm.assignedCompanyCodes);
                                      if (e.target.checked) next.add(code);
                                      else next.delete(code);
                                      setEditForm({ ...editForm, assignedCompanyCodes: [...next] });
                                    }}
                                  />
                                  {company.companyCode}
                                </label>
                              ))}
                            </div>
                          </div>
                          <label className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                            <input type="checkbox" checked={editForm.isActive} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })} />
                            Active user
                          </label>
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <Button variant="primary" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate({ userProfileId })}>
                              Save user
                            </Button>
                            <Button variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
                            {updateMutation.error && (
                              <span className="text-sm text-red-600 dark:text-red-400">{(updateMutation.error as Error).message}</span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )
      )}
    </div>
  );
}

function Field(props: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
      />
    </label>
  );
}
